import dateFormat from 'dateformat'

export const SQM_PER_ACRE = 4046.8564

// Same palette as AEBackend SprayQualityReport/reportFormat.js, so the page and the PDF match
export const COLORS = {
    coveredOnce: '#4CC37A',
    overlap2x: '#F5A524',
    overlap3x: '#A855F7',
    missed: '#E5604D',
    offTarget: '#8A94A6',
    target: '#2F6FDE',
    field: '#E53935',
    sprayArea: '#E8890C',
    path: '#4B5B7A',
    fieldOutline: '#8A94A6',
    fieldFill: '#B4D49A',
    amber: '#F5A524',
}

// Colours drawn into the ready-made map images from the analysis service, for their legends
export const IMAGE_COLORS = {
    coveredOnce: '#52A852',
    overlap2x: '#FCD84A',
    overlap3x: '#F7931A',
    missed: '#E5493F',
    target: '#1E88E5',
    fieldOutline: '#78909C',
    path: '#0F1B2D',
    routeField: '#FF2020',
    routeSprayArea: '#FFE600',
    routePath: '#FFFFFF',
}

const GRADE_COLORS = { A: '#2E9E5B', B: '#4CC37A', C: '#E8A317', D: '#F08A24', F: '#E53935' }

export const isNum = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v))

export const fmtNum = (v, digits = 0) => isNum(v)
    ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : 'NA'

export const fmtPct = (v, digits = 1) => isNum(v) ? `${Number(v).toFixed(digits)}%` : 'NA'

export const toAcres = (m2) => isNum(m2) ? Number(m2) / SQM_PER_ACRE : undefined

export const fmtAcres = (m2, digits = 2) => isNum(m2) ? `${toAcres(m2).toFixed(digits)} acres` : 'NA'

export const fmtDate = (d, mask = 'dd mmm yyyy') => d ? dateFormat(d, mask) : 'NA'

export const fmtValue = (v, unit = '') => (isNum(v) || (v && typeof v === 'string')) ? `${v}${unit}` : 'NA'

export const fmtDuration = (seconds) => {
    if (!isNum(seconds)) return 'NA'
    const s = Math.round(Number(seconds))
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60
    return h ? `${h}h ${m}m ${sec}s` : `${m}m ${sec}s`
}

const safeColor = (c, fallback) => /^#[0-9A-Fa-f]{6}$/.test(c || '') ? c : fallback

export const gradeColor = (score) => safeColor(score?.grade_color, GRADE_COLORS[score?.grade] || '#8A94A6')

// '#E53935', 0.1 → 'rgba(229,57,53,0.1)', for the light badge backgrounds
export const tint = (hex, alpha) => {
    const n = parseInt(safeColor(hex, '#8A94A6').slice(1), 16)
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`
}

// "Nashik, Nashik, Nashik Division, Maharashtra, India" → "Nashik, Maharashtra" (place and state)
export const shortLocation = (location) => {
    const parts = String(location || '').split(',').map(p => p.trim()).filter(Boolean)
    if (!parts.length) return 'NA'
    const state = parts.length > 2 ? parts[parts.length - 2] : parts[1]
    return state && state !== parts[0] ? `${parts[0]}, ${state}` : parts[0]
}

// Score breakdown bars: green from 90% of the points, amber from 50%, red below
export const factorColor = (factor) => {
    const ratio = factor?.points_possible ? factor.points_earned / factor.points_possible : 0
    return ratio >= 0.9 ? COLORS.coveredOnce : ratio >= 0.5 ? COLORS.amber : COLORS.missed
}

// Small areas read better in m² than as "0.01 acres"
export const fmtArea = (m2) => {
    if (!isNum(m2)) return 'NA'
    return toAcres(m2) < 0.01 ? `${fmtNum(m2)} m²` : fmtAcres(m2)
}

// 12 → "12%", 12.5 → "12.5%"
export const fmtPctShort = (v) => isNum(v) ? `${Number(Number(v).toFixed(1))}%` : 'NA'

const ESTIMATED_BOUNDARY_NOTE = {
    title: 'No spray boundary was saved for this flight.',
    text: 'The target area was estimated as the outline around all sprayed passes. Gaps inside that outline count as missed, but ground outside it that should also have been sprayed cannot be detected. Store the plan boundary with the flight for an exact result.',
}

// ---------- readers for both report layouts (schema 2.1 top-level blocks, 2.2 nested under "summary") ----------

export const getSummary = (report) => {
    const s = report?.summary
    const paragraphs = Array.isArray(s?.paragraphs) ? s.paragraphs : Array.isArray(s?.coverage) ? s.coverage : null
    const summary = paragraphs
        ? { paragraphs, key_finding: s.key_finding || s.score_calculation?.this_flight || '', notes: s.notes }
        : buildSummary(report)
    if (!summary.notes && report?.field_dimensions?.target_spray_area?.is_estimated) summary.notes = [ESTIMATED_BOUNDARY_NOTE]
    return summary
}

export const getYieldImpact = (report) => {
    const y = report?.yield_impact || report?.summary?.yield_impact || {}
    const fallback = buildYieldImpact(report)
    const level = y.risk_level || y.risk
    return {
        risk_level: level || fallback.risk_level,
        risk_label: y.risk_label || fallback.risk_label,
        headline: y.headline || fallback.headline,
        items: y.items || y.points || [],
    }
}

export const getSuggestions = (report) => report?.improvement_suggestions || report?.summary?.improvement_suggestions || []

// ---------- text fallbacks, used when the API does not send ready-made text ----------

export const buildSummary = (report) => {
    const cov = report?.coverage || {}
    const target = report?.field_dimensions?.target_spray_area || {}
    const score = report?.spraying_efficiency_score || {}
    const paragraphs = []
    if (isNum(cov.target_area_m2)) {
        paragraphs.push(`The drone was meant to spray about ${fmtNum(cov.target_area_m2)} m²${target.is_estimated ? ', an area estimated from where the drone sprayed, because no spray boundary was saved with this flight' : ''}. ${fmtPct(cov.sprayed_at_least_once?.pct_of_target, 0)} of it received spray at least once.`)
    }
    if (isNum(cov.missed?.area_m2)) {
        paragraphs.push(`About ${fmtNum(cov.missed.area_m2)} m² (${fmtPct(cov.missed.pct_of_target, 0)}) was not sprayed at all and is shown in red on the map.${isNum(cov.missed_detail?.largest_patch_m2) ? ` The biggest single unsprayed patch is about ${fmtNum(cov.missed_detail.largest_patch_m2)} m².` : ''}`)
    }
    if (isNum(cov.overlapped_total?.area_m2)) {
        paragraphs.push(`${fmtPct(cov.overlapped_total.pct_of_target, 0)} of the area (${fmtNum(cov.overlapped_total.area_m2)} m²) was sprayed more than once (yellow and orange), which means extra chemical on those spots.`)
    }
    if (isNum(score.score)) {
        paragraphs.push(`Overall the flight scores ${score.score} out of ${score.max_score || 100}, grade ${score.grade}: ${(score.verdict || '').toLowerCase()}.`)
    }
    const lost = (score.factors || [])
        .map(f => ({ name: f.name, lost: (f.points_possible || 0) - (f.points_earned || 0) }))
        .sort((a, b) => b.lost - a.lost)
        .slice(0, 2)
        .filter(f => f.lost > 0)
    const keyFinding = lost.length
        ? `For this flight, most points were lost on ${lost.map(f => `${f.name.toLowerCase()} (${f.lost.toFixed(1)} points lost)`).join(' and ')}.`
        : ''
    const notes = target.is_estimated ? [ESTIMATED_BOUNDARY_NOTE] : []
    return { paragraphs, key_finding: keyFinding, notes }
}

export const buildYieldImpact = (report) => {
    const missedPct = report?.coverage?.missed?.pct_of_target
    const level = !isNum(missedPct) ? 'unknown' : missedPct >= 30 ? 'high' : missedPct >= 10 ? 'medium' : 'low'
    const labels = { high: 'High risk', medium: 'Medium risk', low: 'Low risk', unknown: 'Unknown' }
    const headlines = {
        high: 'A large share of the field was not treated properly; yield is at real risk if pest, disease or weed pressure is high.',
        medium: 'Part of the field was not treated properly; watch the untreated patches for pest, disease or weed pressure.',
        low: 'Almost all of the field was treated; the effect on yield should be small.',
        unknown: '',
    }
    return { risk_level: level, risk_label: labels[level], headline: headlines[level], items: [] }
}

export const RISK_COLORS = { high: '#E53935', medium: '#F08A24', low: '#2E9E5B', unknown: '#8A94A6' }


const R = 6378137

export const toMercator = ([lat, lon]) => [
    R * lon * Math.PI / 180,
    R * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)),
]

/**
 * Builds a projector that fits every given [lat, lon] into a width x height box with padding,
 * keeping the aspect ratio. The returned bbox has the same aspect as the box, so it can be
 * passed straight to a map image export without the server stretching it.
 */
export const createProjector = (latLngs, width, height, paddingRatio = 0.08) => {
    const pts = latLngs.map(toMercator)
    let minX = Math.min(...pts.map(p => p[0])), maxX = Math.max(...pts.map(p => p[0]))
    let minY = Math.min(...pts.map(p => p[1])), maxY = Math.max(...pts.map(p => p[1]))
    let w = Math.max(maxX - minX, 1), h = Math.max(maxY - minY, 1)
    minX -= w * paddingRatio; maxX += w * paddingRatio; minY -= h * paddingRatio; maxY += h * paddingRatio
    w = maxX - minX; h = maxY - minY
    const boxAspect = width / height
    if (w / h > boxAspect) {
        const extra = w / boxAspect - h
        minY -= extra / 2; maxY += extra / 2
    } else {
        const extra = h * boxAspect - w
        minX -= extra / 2; maxX += extra / 2
    }
    const scale = width / (maxX - minX)
    // Mercator units are stretched by 1 / cos(lat) compared to ground metres
    const midLat = latLngs.reduce((s, p) => s + p[0], 0) / latLngs.length
    const pxPerMetre = scale / Math.cos(midLat * Math.PI / 180)
    const project = (latLng) => {
        const [x, y] = toMercator(latLng)
        return [(x - minX) * scale, (maxY - y) * scale]
    }
    return { project, pxPerMetre, bbox: [minX, minY, maxX, maxY] }
}

export const toSvgPoints = (latLngs, project) => latLngs.map(p => project(p).map(v => v.toFixed(1)).join(',')).join(' ')

// Picks a round scale bar length that is about a quarter of the map width
export const scaleBarMetres = (pxPerMetre, mapWidth) => {
    const target = mapWidth / 4 / pxPerMetre
    const steps = [5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000]
    return steps.reduce((best, s) => Math.abs(s - target) < Math.abs(best - target) ? s : best, steps[0])
}
